import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";
import { api, track, getUtm, captureUtm, deriveSource } from "@/lib/api";

const CONSENT_WORDING = "Yes, I'd like to receive useful property updates, opportunities, guides and news from Emerald Crown Property Investments.";
const AUDIENCE_OPTIONS = [
  { value: "investor", label: "I'm looking to invest in property" },
  { value: "vendor", label: "I'm considering selling a property" },
  { value: "both", label: "Both" },
];

export default function ResourceDetail() {
  const { slug } = useParams();
  const [resource, setResource] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", audience: "", marketing_consent: false, website: "" });
  const [errors, setErrors] = useState({});

  useSeo({ title: resource ? `${resource.title} | Emerald Crown` : "Free Resource | Emerald Crown", description: resource?.description || "Download a free property resource from Emerald Crown Property Investments.", path: `/resources/${slug}` });

  useEffect(() => {
    captureUtm();
    api.get(`/resources/${slug}`).then((r) => {
      setResource(r.data);
      const pre = r.data.audience === "Vendor" ? "vendor" : r.data.audience === "Investor" ? "investor" : "";
      setForm((f) => ({ ...f, audience: pre }));
      track("page_view", { page: "resource_detail", resource: r.data.slug });
    }).catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!/^[\d\s()+\-]{7,20}$/.test(form.mobile)) e.mobile = "Please enter a valid mobile number";
    if (!form.audience) e.audience = "Please choose an option";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (form.website) return;
    if (!validate()) return;
    setSubmitting(true);
    const p = new URLSearchParams(window.location.search);
    const utm = { ...getUtm() };
    ["utm_content", "utm_term"].forEach((k) => { const v = p.get(k); if (v) utm[k] = v; });
    try {
      const { data } = await api.post(`/resources/${slug}/submit`, {
        ...form,
        consent_wording: form.marketing_consent ? CONSENT_WORDING : "",
        source: deriveSource(),
        landing_page: window.location.pathname + window.location.search,
        referrer: document.referrer || "",
        utm,
      });
      track("resource_lead", { resource: slug, audience: data.audience });
      setDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) return (
    <div className="pt-32 pb-24 bg-[#071912] min-h-[60vh]"><div className="max-w-xl mx-auto px-4 text-center text-emerald-100/70" data-testid="resource-not-found">
      <h1 className="text-2xl font-serif text-white mb-3">Resource not found</h1>
      <Link to="/resources" className="text-brass underline">Back to all resources</Link>
    </div></div>
  );
  if (!resource) return <div className="pt-32 pb-24 bg-[#071912] min-h-[60vh] text-center text-emerald-100/50">Loading…</div>;

  return (
    <div className="bg-[#071912] min-h-screen" data-testid="resource-detail-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        {!done ? (
          <>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-brass">Free Download</span>
            <h1 className="mt-3 text-3xl sm:text-4xl font-serif font-semibold text-white">{resource.title}</h1>
            <p className="mt-4 text-emerald-100/75 leading-relaxed">{resource.description}</p>

            <form onSubmit={submit} className="mt-8 bg-[#0F2E23] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="resource-form">
              <div className="hidden"><input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First name" error={errors.first_name}>
                  <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="resource-first-name" autoComplete="given-name" className="w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3.5 py-3 text-base sm:text-sm text-white placeholder:text-emerald-100/40 outline-none" />
                </Field>
                <Field label="Last name">
                  <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="resource-last-name" autoComplete="family-name" className="w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3.5 py-3 text-base sm:text-sm text-white placeholder:text-emerald-100/40 outline-none" />
                </Field>
              </div>
              <Field label="Email address" error={errors.email}>
                <input type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="resource-email" className="w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3.5 py-3 text-base sm:text-sm text-white placeholder:text-emerald-100/40 outline-none" />
              </Field>
              <Field label="Mobile number" error={errors.mobile}>
                <input type="tel" inputMode="tel" autoComplete="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="resource-mobile" className="w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3.5 py-3 text-base sm:text-sm text-white placeholder:text-emerald-100/40 outline-none" />
              </Field>
              <div>
                <label className="block text-sm font-medium text-emerald-100/80 mb-2">Which best describes you?</label>
                <div className="space-y-2">
                  {AUDIENCE_OPTIONS.map((o) => (
                    <label key={o.value} data-testid={`resource-audience-${o.value}`} className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${form.audience === o.value ? "border-brass bg-brass/10" : "border-white/15 hover:border-white/30"}`}>
                      <input type="radio" name="audience" checked={form.audience === o.value} onChange={() => set("audience", o.value)} className="accent-[#C59B27] w-4 h-4" />
                      <span className="text-sm text-white">{o.label}</span>
                    </label>
                  ))}
                </div>
                {errors.audience && <p className="text-xs text-red-300 mt-1">{errors.audience}</p>}
              </div>
              <label className="flex items-start gap-3 cursor-pointer pt-1">
                <input type="checkbox" checked={form.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} data-testid="resource-marketing-consent" className="accent-[#C59B27] w-5 h-5 mt-0.5 shrink-0" />
                <span className="text-xs text-emerald-100/70 leading-relaxed">{CONSENT_WORDING}</span>
              </label>
              <button type="submit" disabled={submitting} data-testid="resource-submit" className="w-full flex items-center justify-center gap-2 bg-brass hover:bg-brass-600 disabled:opacity-60 text-emerald-950 font-semibold rounded-full py-3.5 text-base transition-colors">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />} {resource.cta}
              </button>
              <p className="text-[11px] text-emerald-100/50 text-center">We'll email you a copy. By continuing you agree to our <Link to="/privacy-policy" className="underline text-brass">Privacy Policy</Link>. The download is provided whether or not you opt into marketing.</p>
            </form>
          </>
        ) : (
          <ThankYou done={done} />
        )}
      </div>
    </div>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-emerald-100/80 mb-1.5">{label}</label>
    {children}
    {error && <p className="text-xs text-red-300 mt-1">{error}</p>}
  </div>
);

function ThankYou({ done }) {
  const showInvestor = done.audience === "Investor" || done.audience === "Both";
  const showVendor = done.audience === "Vendor" || done.audience === "Both";
  return (
    <div className="text-center" data-testid="resource-thankyou">
      <div className="grid place-items-center w-16 h-16 rounded-full bg-brass/15 mx-auto"><CheckCircle2 className="w-9 h-9 text-brass" /></div>
      <h1 className="mt-6 text-3xl font-serif font-semibold text-white">Thanks — your resource is ready.</h1>
      <p className="mt-3 text-emerald-100/75">Check your inbox for a copy, or download it below.</p>
      <a href={done.download_url} target="_blank" rel="noopener noreferrer" data-testid="resource-download-btn"
        className="mt-7 inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-8 py-3.5 text-base transition-colors">
        <Download className="w-5 h-5" /> DOWNLOAD NOW
      </a>

      <div className="mt-12 grid grid-cols-1 gap-4">
        {showInvestor && (
          <div className="bg-[#0F2E23] border border-white/10 rounded-2xl p-6 text-left" data-testid="cta-investor">
            <h3 className="text-lg font-serif text-white">Looking for your next property investment?</h3>
            <p className="mt-2 text-sm text-emerald-100/70">Tell us your buying criteria and we'll keep you in mind for suitable opportunities.</p>
            <Link to="/investors" className="mt-4 inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors">SUBMIT BUYING CRITERIA <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}
        {showVendor && (
          <div className="bg-[#0F2E23] border border-white/10 rounded-2xl p-6 text-left" data-testid="cta-vendor">
            <h3 className="text-lg font-serif text-white">Thinking about selling a property?</h3>
            <p className="mt-2 text-sm text-emerald-100/70">If you'd like to discuss your property and the options available, tell us a little about it.</p>
            <Link to="/sell" className="mt-4 inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors">DISCUSS MY PROPERTY <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}
      </div>
    </div>
  );
}
